import { Status1, MilestoneMapInterval } from '../types';
import { getStatusForAdjustedInterval } from '@mm-shared/intervals';
import { upsertItem } from './upsertItem';
import { buildWebStatusWrite } from './provenance';
import { getShortIdFromUuid } from '@mm-shared/utils/getShortIdFromUuid';

/**
 * Computes the updated `statuses` array for a single status cell after a key press.
 *
 * This is the shared per-cell logic used by both the multi-cell and single-cell
 * keyboard update paths in Map3's `onKeyPress` handler.
 *
 * @returns The new statuses array, or `null` when the key is not a valid status key.
 */
export function buildStatusUpdateForCell(
  currentStatuses: Status1[],
  colIdx: number,
  visibleIntervals: Array<{ start: Date; end: Date }>,
  mapInterval: MilestoneMapInterval,
  effectiveZoomLevel: MilestoneMapInterval,
  pressedKey: string,
  isDeleteKey: boolean,
  now: string,
  userName?: string,
): Status1[] | null {
  const validStatusCodes: Status1['statusCode'][] = ['g', 'y', 'r', 'p', '?'];
  const lowerKey = pressedKey.toLowerCase() as Status1['statusCode'];
  const isStatusKey = validStatusCodes.includes(lowerKey);

  if (!isStatusKey && !isDeleteKey) return null;

  const statusIndex = colIdx - 1; // col 1 = statusIndex 0
  if (statusIndex < 0 || statusIndex >= visibleIntervals.length) return null;

  const interval = visibleIntervals[statusIndex];
  const forIntervalEnding = interval.end.toISOString();

  const existingStatus = getStatusForAdjustedInterval(
    currentStatuses,
    interval,
    mapInterval,
    effectiveZoomLevel,
  );

  let statuses = [...currentStatuses];

  if (isDeleteKey) {
    if (existingStatus?.uuid) {
      statuses = statuses.filter((s) => s.uuid !== existingStatus.uuid);
    }
  } else {
    const uuid = existingStatus?.uuid || crypto.randomUUID();
    const newStatus = buildWebStatusWrite(
      existingStatus,
      {
        uuid,
        shortId: existingStatus?.shortId || getShortIdFromUuid(uuid),
        forIntervalEnding,
        statusCode: lowerKey,
      },
      userName,
      now,
    );
    statuses = upsertItem(statuses, newStatus, (s) => s.uuid === uuid);
  }

  return statuses;
}
